import { isChunkLoadError } from '@core/app-version/chunk-load-error.js';
import { useState } from 'react';

import { DebugBlock } from '../components/debug-block.js';
import { t } from '../translations.js';
import { ErrorPanel } from './error-panel.js';
import { NewVersionPanel } from './new-version-panel.js';

/**
 * Last-resort UI when something inside the app shell throws during render — the
 * `Sentry.ErrorBoundary` fallback (see `error-boundary.tsx`). Wraps `ErrorPanel`
 * with the crash title + Reload, shows the thrown message in a muted `<pre>`
 * (`ErrorPanel`'s `errors` prop only accepts `AppError[]`, and a render-time
 * crash isn't one), and — when Sentry returns an `eventId` — a `DebugBlock`
 * keyed to that event, so a quoted reference opens the crash issue (with stack)
 * directly in Sentry. Reload is the safe default: the QueryClient is cleared at
 * the same time (see `error-boundary.tsx`).
 *
 * A chunk-load failure (stale build after a deploy) is not a crash in the
 * app's code: it renders `NewVersionPanel` instead — see
 * `docs/features/chunk-load-recovery/design.md`.
 *
 * Named for its situation, like every other wrapper (`MeBootstrap`,
 * `SessionInvalidated`, `Conflict`). See
 * `docs/features/error-surface-architecture/design.md`.
 */
export function AppCrashPanel({
  error,
  eventId,
  onReload,
}: {
  error: unknown;
  eventId?: string;
  onReload: () => void;
}) {
  // Frozen at mount ≈ crash time (the fallback mounts when the boundary catches).
  const [timestamp] = useState(() => new Date());

  if (isChunkLoadError(error)) return <NewVersionPanel />;

  const message =
    error instanceof Error && typeof error.message === 'string' && error.message
      ? error.message
      : null;

  return (
    <div>
      <ErrorPanel
        title={t.app.error.title}
        description={t.app.error.description}
        primaryAction={{
          label: t.app.error.reload,
          onClick: onReload,
        }}
        testId="app-crash-panel"
      />
      {message !== null ? (
        <pre
          data-testid="app-crash-error-message"
          className="mx-auto -mt-2 max-w-md overflow-auto whitespace-pre-wrap break-words rounded-md border border-border bg-muted p-3 text-xs text-muted-foreground"
        >
          {message}
        </pre>
      ) : null}
      {eventId ? (
        <div className="mx-auto mt-2 max-w-md">
          <DebugBlock code="app_crash" reference={eventId} timestamp={timestamp} />
        </div>
      ) : null}
    </div>
  );
}
